// ko landing pages: see config/localeHubs.ts for the rules and
// config/hubs/ko.json for the slug of each page type (keep them in step).
//
// Volumes are OpenSEO, South Korea, monthly (Oct 2026); phrasing follows
// Google autocomplete (hl=ko, gl=kr). Korean searchers add 무료, 사이트,
// 프로그램 and 디시 to almost everything; "free" is answered honestly in each
// FAQ, never promised.
//
// Skipped on purpose:
// - "AI 목소리" (5.4k) and "ai 목소리 만들기" (1k): text-to-speech intent
//   (브루, ElevenLabs), not ours.
// - "AI 노래" (4.4k) on its own: autocomplete is led by "ai 노래 만들기"
//   (Suno-style song generators). Only its cover variants are targeted:
//   "ai 노래 커버" 480, "ai 노래 커버 사이트" 480.
// - "실시간 목소리 변조", 디스코드 and 마이크 variants want live changing.
//   Jammable converts recorded audio; the voice changer FAQ says so.
import type { HubCategory, HubConfig } from "../hubPages";

const CATEGORIES_KO: HubCategory[] = [
  { label: "음악", filter: "music" },
  { label: "애니메이션", filter: "anime" },
  { label: "만화", filter: "cartoon" },
  { label: "게임", filter: "gaming" },
  { label: "K-pop", filter: "korean" },
  { label: "유명인", filter: "public figure" },
];

const FREE_KO = {
  q: "Jammable은 무료인가요?",
  a: "모든 목소리를 둘러보고 샘플 커버를 듣는 건 무료예요. 커버 만들기, 목소리 변조, 목소리 학습(복제), 텍스트 음성 변환(TTS)은 요금제가 필요해요. 현재 요금은 요금제 페이지에서 확인하세요.",
};

export const HUBS: Record<string, HubConfig> = {
  // "보이스 체인저" / "보이스체인저" 3,600 (KD 0), "목소리 변조" 2,900 (KD 0),
  // "목소리 변조 프로그램" 720, "목소리 변조 사이트" 320, "AI 목소리 변조" /
  // "목소리 변조 ai" 260, "목소리 변조 앱" 260. Autocomplete: "보이스 체인저
  // 여자 목소리", "남자 목소리 여자 목소리로 변환", "목소리 바꾸기 ai".
  "voice-changer": {
    slug: "voice-changer",
    group: "voice-changer",
    path: "/ko/voice-changer",
    locale: "ko",
    h1: "AI 보이스 체인저",
    highlight: "보이스 체인저",
    title: "AI 보이스 체인저 · 목소리 변조 사이트 | Jammable",
    metaDescription:
      "AI 보이스 체인저로 목소리를 가수, 캐릭터, 유명인의 목소리로 바꿔 보세요. 6만 개 이상의 AI 목소리, 설치 없이 웹에서 녹음하거나 업로드해서 목소리 변조.",
    heroSub:
      "오디오를 녹음하거나 업로드하고, 원하는 목소리를 고르면 몇 초 만에 변조된 목소리를 들을 수 있어요.",
    intro:
      "AI 목소리 변조는 단순히 음성을 뒤틀지 않아요. 내 목소리를 실제 다른 목소리로 바꿔 줘요. 가수, 애니메이션 캐릭터, 유명인, 또는 직접 학습시킨 목소리까지 고를 수 있어요.",
    appName: "Jammable AI 보이스 체인저",
    steps: [
      {
        title: "녹음하거나 업로드",
        body: "마이크로 녹음하거나 바꾸고 싶은 오디오 파일을 올리세요.",
      },
      {
        title: "목소리 고르기",
        body: "캐릭터와 애니메이션부터 가수와 유명인까지, 6만 개 이상의 AI 목소리 중에서 골라요.",
      },
      {
        title: "목소리 변조",
        body: "몇 초 만에 새 목소리로 바뀐 오디오가 완성돼요. 바로 다운로드할 수 있어요.",
      },
    ],
    faqs: [
      {
        q: "AI 보이스 체인저란 무엇인가요?",
        a: "녹음된 오디오를 AI 음성 변환으로 다른 목소리처럼 들리게 바꿔 주는 도구예요. 효과음을 입히는 기존 목소리 변조 프로그램과 달리, 결과물이 고른 목소리 그대로 들려요.",
      },
      {
        q: "AI로 목소리를 바꾸려면 어떻게 하나요?",
        a: "오디오를 녹음하거나 업로드하고, 원하는 목소리를 고른 뒤 만들기를 누르세요. Jammable이 오디오를 변환해 깨끗한 결과물을 다운로드할 수 있게 해 줘요. 프로그램이나 앱을 설치할 필요 없이 웹사이트에서 바로 할 수 있어요.",
      },
      {
        q: "실시간으로 변조되나요? 디스코드나 통화에서도 쓸 수 있나요?",
        a: "Jammable은 녹음하거나 업로드한 오디오를 변환해요. 방송이나 통화 중 실시간 변조는 지원하지 않아요. 그 대신 실시간 보이스 체인저보다 훨씬 자연스러운 결과를 얻을 수 있고, 완성된 오디오는 다운로드해서 원하는 곳에 쓸 수 있어요.",
      },
      {
        q: "남자 목소리를 여자 목소리로 바꿀 수 있나요?",
        a: "네. 여자 가수나 캐릭터의 목소리를 고르면 돼요. 음높이(피치) 설정에서 남자 → 여자 또는 여자 → 남자를 선택하면 더 자연스럽게 들려요.",
      },
      FREE_KO,
    ],
    categories: CATEGORIES_KO,
    related: [
      { label: "AI 커버 만들기", href: "/ko/ai-cover" },
      { label: "내 목소리 AI 만들기", href: "/ko/voice-clone" },
      { label: "모든 AI 목소리", href: "/models" },
    ],
  },

  // "AI 커버" 720 (KD 0), "ai cover" 480, "ai 커버 만들기" 480, "ai 노래 커버"
  // 480, "ai 노래 커버 사이트" 480, "ai 커버 사이트" 260, "ai 커버곡 만들기"
  // 210, "ai 커버곡" 110. Autocomplete: "아이돌 ai 커버 만들기", "가수 ai 커버
  // 만들기", "내 목소리 ai 커버 만들기". The grid shows K-pop voices.
  "ai-cover": {
    slug: "ai-cover",
    group: "cover",
    path: "/ko/ai-cover",
    locale: "ko",
    h1: "AI 커버 만들기",
    highlight: "AI 커버",
    title: "AI 커버 만들기: AI 노래 커버 사이트 | Jammable",
    metaDescription:
      "아이돌, 가수, 캐릭터 목소리로 AI 커버곡을 만들어 보세요. 6만 개 이상의 AI 목소리로 만드는 AI 노래 커버, 스튜디오급 품질로 몇 초 만에 완성돼요.",
    heroSub:
      "목소리를 고르고, 사용할 권리가 있는 오디오를 추가하면 Jammable이 몇 초 만에 AI 커버를 만들어 줘요.",
    intro:
      "예전에는 노래의 목소리를 바꾸려면 스튜디오와 몇 시간의 믹싱이 필요했어요. Jammable에서는 목소리를 고르고 오디오를 올리기만 하면 AI가 보컬을 분리하고, 변환하고, 다시 믹스해 줘요.",
    appName: "Jammable AI 커버",
    steps: [
      {
        title: "목소리 고르기",
        body: "K-pop 아이돌, 가수, 래퍼, 애니메이션 캐릭터 등 6만 개 이상의 AI 목소리, 또는 직접 학습시킨 목소리.",
      },
      {
        title: "오디오 추가",
        body: "파일을 업로드하거나, 직접 노래를 녹음하거나, AI로 오리지널 곡을 만들어요.",
      },
      {
        title: "커버 완성",
        body: "Jammable이 목소리를 변환하고 믹스해요. 다운로드해서 TikTok이나 YouTube에 공유하세요.",
      },
    ],
    faqs: [
      {
        q: "AI 커버란 무엇인가요?",
        a: "AI로 다른 목소리가 부른 것처럼 만든 커버곡이에요. 음원이나 내 노래를 가수, 캐릭터, 또는 직접 학습시킨 목소리로 바꾸고, 멜로디와 가사는 그대로 유지해요.",
      },
      {
        q: "AI 커버곡은 어떻게 만드나요?",
        a: "Jammable에서 목소리를 고르고 오디오를 올린 뒤 만들기를 누르세요. 보컬과 반주를 분리하고, 고른 목소리로 변환한 다음 1분 안에 다시 믹스해 줘요. 사용할 권리가 있는 오디오만 사용하세요.",
      },
      {
        q: "내 목소리로 AI 커버를 만들 수 있나요?",
        a: "네. 깨끗한 오디오 6분 이상으로 내 목소리 모델을 학습시키면 어떤 커버에든 사용할 수 있어요.",
      },
      {
        q: "아이돌 목소리 AI 커버도 만들 수 있나요?",
        a: "네. 커뮤니티가 만든 K-pop 아이돌과 가수 목소리가 많아요. 공식 목소리가 아니며 아티스트와 관련이 없으니, 존중하는 마음으로 사용하고 다른 사람을 속이는 데 쓰지 마세요.",
      },
      FREE_KO,
    ],
    categories: CATEGORIES_KO,
    related: [
      { label: "AI 보이스 체인저", href: "/ko/voice-changer" },
      { label: "내 목소리 AI 만들기", href: "/ko/voice-clone" },
      { label: "모든 AI 목소리", href: "/models" },
    ],
    voices: { badge: "korean", allTime: true },
  },

  // "목소리 복제" 70 alone, but the training intent is bigger: "내 목소리 ai
  // 만들기" 590 (KD 0), "내 목소리 ai 노래 만들기" 320, "ai 목소리 학습" 260,
  // "목소리 학습 ai" 170, "목소리 복제 ai" 140. Autocomplete: "내 목소리로
  // 노래 커버", "목소리 학습시키기", "rvc 모델".
  "voice-clone": {
    slug: "voice-clone",
    group: "clone",
    path: "/ko/voice-clone",
    locale: "ko",
    h1: "내 목소리 AI 만들기",
    highlight: "AI",
    title: "내 목소리 AI 만들기: AI 목소리 학습·복제 | Jammable",
    metaDescription:
      "6분 분량의 오디오로 내 목소리를 AI에 학습시켜 보세요. 복제한 목소리로 AI 노래 커버와 텍스트 음성 변환까지. RVC 모델 업로드도 지원해요.",
    heroSub:
      "깨끗한 오디오를 6분 이상 올리면 Jammable이 그 목소리를 학습한 모델을 만들어 줘요. 커버와 텍스트 음성 변환에 바로 쓸 수 있어요.",
    intro:
      "AI 목소리 복제는 목소리가 어떻게 들리는지 배우는 모델을 학습시키는 거예요. 학습이 끝나면 그 목소리로 원하는 노래를 부르거나 말하게 할 수 있어요. 내 목소리나 사용 허락을 받은 목소리만 복제하세요.",
    appName: "Jammable AI 목소리 학습",
    steps: [
      {
        title: "오디오 업로드",
        body: "노래하거나 말하는 목소리 6분 이상. 잡음과 긴 무음이 없을수록 좋아요.",
      },
      {
        title: "모델 학습",
        body: "Jammable이 대신 학습시켜요. 그래픽카드도, 기술 지식도 필요 없어요.",
      },
      {
        title: "내 목소리 사용",
        body: "커버를 만들고, 오디오를 변환하고, 텍스트를 입력해 복제한 목소리로 들어 보세요.",
      },
    ],
    faqs: [
      {
        q: "AI로 내 목소리를 어떻게 만드나요?",
        a: "깨끗한 오디오를 6분 이상 올리고 이름을 정하면 Jammable이 모델을 학습시켜요. 학습이 끝나면 라이브러리에 목소리가 추가되어 바로 사용할 수 있어요.",
      },
      {
        q: "목소리 학습에 오디오가 얼마나 필요한가요?",
        a: "배경 음악이나 긴 무음이 없는 깨끗한 목소리가 최소 6분 필요해요. 오디오가 길고 품질이 좋을수록 더 닮은 결과가 나와요.",
      },
      {
        q: "갖고 있는 RVC 모델을 올릴 수 있나요?",
        a: "네. 라이브러리에서 .pth와 .index 파일을 올리면 목소리를 바로 사용할 수 있어요.",
      },
      {
        q: "다른 사람의 목소리를 복제해도 되나요?",
        a: "본인의 허락이 있을 때만 가능해요. 내 목소리나 사용할 권리가 있는 목소리만 복제하세요.",
      },
      FREE_KO,
    ],
    categories: CATEGORIES_KO,
    related: [
      { label: "목소리 학습 시작하기", href: "/train" },
      { label: "AI 커버 만들기", href: "/ko/ai-cover" },
      { label: "AI 보이스 체인저", href: "/ko/voice-changer" },
    ],
  },
};
